import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch } from 'react-redux';
import s from './FilterOptions.module.css';
import {
  applyGradebookFilters,
  getGradebook,
  getGradebookInstructor,
  updateGradebook,
} from '../../../../actions/gradebook';
import MLButton from '../../../MLButton';
import ResourceTypeFilterSection from './ResourceTypeFilterSection';
import ExceptionGroupFilterSection from './ExceptionGroupFilterSection';
import DateRangeFilterSection from './DateRangeFilterSection';

import {
  courseIdSelector,
  exceptionGroupsSelector,
  filterTypesSelector,
  isGradebookV2ActiveSelector,
  searchDateFromSelector,
  searchDateToSelector,
  visibleFilterSelector,
} from '../../../../selectors/search-gradebook';
import {
  EXCEPTION_GROUPS_GB_FILTER_SECTION_ID,
  RESOURCE_TYPE_GB_FILTER_SECTION_ID,
} from '../../../../utils/constants';
import { useExceptionGroupsQuery } from '../../../../core/lms';

function FilterOptions({
  courseId,
  visibleFilter,
  exceptionGroups,
  filterTypes,
  searchDateFrom,
  searchDateTo,
  isGradebookV2Active,
}) {
  const dispatch = useDispatch();
  const { data: exceptionGroupOptions } = useExceptionGroupsQuery(courseId);

  const removeAllFilters = () => {
    dispatch(
      updateGradebook({
        exceptionGroups: [],
        filterTypes: [],
        searchDateFrom: null,
        searchDateTo: null,
      }),
    );
    if (isGradebookV2Active) {
      dispatch(
        getGradebookInstructor(courseId, {
          exceptionGroupIds: [],
          filterTypes: [],
        }),
      );
    } else {
      dispatch(getGradebook(courseId));
    }
  };

  const isAnyFilterOptionSelected = () => {
    return (
      exceptionGroups.length > 0 ||
      filterTypes.length > 0 ||
      (searchDateTo && searchDateFrom)
    );
  };

  const getVisibleFilterOverride = () => {
    // If no exception group options, show resource type filter by default
    if (
      visibleFilter === EXCEPTION_GROUPS_GB_FILTER_SECTION_ID &&
      exceptionGroupOptions.length === 0
    ) {
      return RESOURCE_TYPE_GB_FILTER_SECTION_ID;
    }

    return visibleFilter;
  };
  const visibleFilterOverride = getVisibleFilterOverride();

  return (
    <div
      className={
        isGradebookV2Active
          ? `${s.filterOptions} ${s.filterOptionsV2}`
          : s.filterOptions
      }
      data-test-id="gb-filters-container"
    >
      <div className={s.filterOptionsHeader}>
        <div className={s.filterOptionsHeaderText}>Filter Options</div>
        <MLButton
          disabled={!isAnyFilterOptionSelected}
          btnType="secondary"
          testId="cancel-filters"
          onClick={() => removeAllFilters()}
        >
          <span>Clear</span>
        </MLButton>
        <MLButton
          btnType="primary"
          testId="apply-filters"
          onClick={() => dispatch(applyGradebookFilters(courseId))}
        >
          <span>Apply Filters</span>
        </MLButton>
      </div>
      <div className={s.filterSections}>
        {exceptionGroupOptions.length > 0 ? (
          <ExceptionGroupFilterSection
            dispatch={dispatch}
            courseId={courseId}
            visibleFilter={visibleFilterOverride}
            exceptionGroups={exceptionGroups}
          />
        ) : null}
        <ResourceTypeFilterSection
          dispatch={dispatch}
          visibleFilter={visibleFilterOverride}
          resourceTypes={filterTypes}
        />
        <DateRangeFilterSection
          dispatch={dispatch}
          visibleFilter={visibleFilterOverride}
          searchDateFrom={searchDateFrom}
          searchDateTo={searchDateTo}
        />
      </div>
    </div>
  );
}

FilterOptions.propTypes = {
  courseId: PropTypes.string.isRequired,
  visibleFilter: PropTypes.string.isRequired,
  exceptionGroups: PropTypes.array.isRequired,
  filterTypes: PropTypes.array.isRequired,
  searchDateFrom: PropTypes.object,
  searchDateTo: PropTypes.object,
  isGradebookV2Active: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    courseId: courseIdSelector(state),
    visibleFilter: visibleFilterSelector(state),
    exceptionGroups: exceptionGroupsSelector(state),
    filterTypes: filterTypesSelector(state),
    searchDateFrom: searchDateFromSelector(state),
    searchDateTo: searchDateToSelector(state),
    isGradebookV2Active: isGradebookV2ActiveSelector(state),
  };
}

export default connect(mapStateToProps)(FilterOptions);
